/**
 * HearthChatPanel — proximity-bound ephemeral chat overlay at hearths.
 *
 * Round 14 Phase E (AGE-1227). Players within ~10 tiles of a hearth can
 * exchange short text messages. Messages are ephemeral (no persistence) and
 * fade after FADE_AFTER_MS.
 *
 * Toggled via T key (routed through HudState HEARTH_CHAT_TOGGLE event).
 * Uses a hidden HTML <input> element for text entry — Phaser has no native
 * text input widget. The input is created/destroyed with the panel.
 *
 * Follows the Container + show/hide pattern used by InventoryPanel.
 */

import * as Phaser from 'phaser';
import {
  C5_HANKO,
  C5_INK,
  C5_INK_DIM,
  C5_PAPER,
  C5_PAPER_DK,
  hexToCSS,
} from './palette';
import { drawPaperSlip } from './c5/paperMaterial';
import { getString } from './locale';
import { FontManager } from './FontManager';
import {
  HEARTH_WHISPER_MAX_LENGTH,
  type HearthWhisperBroadcastEvent,
} from '@game/shared';

const PANEL_DEPTH = 2500;
const FADE_MS = 150;
const PANEL_WIDTH = 320;
const PANEL_HEIGHT = 200;
const MARGIN = 12;
const LINE_HEIGHT = 16;
const MAX_VISIBLE_LINES = 8;
const FADE_AFTER_MS = 8_000;
const INPUT_HEIGHT = 24;

interface ChatLine {
  text: Phaser.GameObjects.Text;
  createdAt: number;
}

export class HearthChatPanel {
  private scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container;
  private visible = false;
  private inputActive = false;

  // Chat log lines
  private lines: ChatLine[] = [];
  private logContainer: Phaser.GameObjects.Container;

  // Input
  private inputBg!: Phaser.GameObjects.Rectangle;
  private inputText!: Phaser.GameObjects.Text;
  private inputCursor!: Phaser.GameObjects.Rectangle;
  private currentInput = '';
  private cursorTimer: Phaser.Time.TimerEvent | null = null;

  // Keyboard capture
  private keyListener: ((event: KeyboardEvent) => void) | null = null;

  // Callback to send message to server
  private onSend: ((content: string) => void) | null = null;

  // Hint text
  private hintText!: Phaser.GameObjects.Text;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.container = scene.add.container(0, 0);
    this.container.setScrollFactor(0);
    this.container.setDepth(PANEL_DEPTH);
    this.container.setVisible(false);
    this.container.setAlpha(0);

    this.logContainer = scene.add.container(0, 0);
    this.container.add(this.logContainer);

    this.createPanel();
  }

  setOnSend(cb: (content: string) => void): void {
    this.onSend = cb;
  }

  show(): void {
    if (this.visible) return;
    this.visible = true;
    this.container.setVisible(true);
    this.scene.tweens.add({
      targets: this.container,
      alpha: 1,
      duration: FADE_MS,
    });
  }

  hide(): void {
    if (!this.visible) return;
    this.deactivateInput();
    this.visible = false;
    this.scene.tweens.add({
      targets: this.container,
      alpha: 0,
      duration: FADE_MS,
      onComplete: () => {
        this.container.setVisible(false);
      },
    });
  }

  toggle(): void {
    if (this.visible) {
      this.hide();
    } else {
      this.show();
    }
  }

  isVisible(): boolean {
    return this.visible;
  }

  isInputFocused(): boolean {
    return this.inputActive;
  }

  /** Receive a broadcast whisper message and display it. */
  addMessage(event: HearthWhisperBroadcastEvent): void {
    const { width } = this.scene.scale;
    const px = MARGIN;
    const maxWidth = PANEL_WIDTH - MARGIN * 2;

    const displayText = `${event.senderName}: ${event.content}`;
    const line = this.scene.add.text(px, 0, displayText, {
      fontFamily: FontManager.fontFamily(),
      fontSize: '10px',
      color: hexToCSS(C5_INK),
      wordWrap: { width: maxWidth },
    });

    const chatLine: ChatLine = {
      text: line,
      createdAt: Date.now(),
    };

    this.logContainer.add(line);
    this.lines.push(chatLine);

    // Trim excess lines
    while (this.lines.length > MAX_VISIBLE_LINES) {
      const removed = this.lines.shift()!;
      removed.text.destroy();
    }

    this.relayoutLines();

    // Auto-show panel when a message arrives
    if (!this.visible) {
      this.show();
    }
  }

  /** Called every frame from UIScene update to fade old messages. */
  update(): void {
    if (!this.visible) return;
    const now = Date.now();
    let changed = false;

    for (let i = this.lines.length - 1; i >= 0; i--) {
      const line = this.lines[i];
      const age = now - line.createdAt;
      if (age > FADE_AFTER_MS) {
        line.text.destroy();
        this.lines.splice(i, 1);
        changed = true;
      } else if (age > FADE_AFTER_MS - 2000) {
        // Fade out over last 2 seconds
        const fadeProgress = (age - (FADE_AFTER_MS - 2000)) / 2000;
        line.text.setAlpha(1 - fadeProgress);
      }
    }

    if (changed) {
      this.relayoutLines();
    }

    // Blink cursor
    if (this.inputActive && this.inputCursor) {
      const blinkOn = Math.floor(now / 530) % 2 === 0;
      this.inputCursor.setVisible(blinkOn);
    }
  }

  /** Activate the text input (Enter key pressed while panel visible). */
  activateInput(): void {
    if (this.inputActive) return;
    this.inputActive = true;
    this.currentInput = '';
    this.inputText.setText('');
    this.inputBg.setStrokeStyle(2, C5_HANKO);
    this.inputCursor.setVisible(true);
    this.hintText.setVisible(false);

    // Capture keyboard events directly
    this.keyListener = (e: KeyboardEvent) => {
      // Prevent game from processing these keys
      e.stopPropagation();

      if (e.key === 'Escape') {
        this.deactivateInput();
        return;
      }

      if (e.key === 'Enter') {
        this.submitInput();
        return;
      }

      if (e.key === 'Backspace') {
        this.currentInput = this.currentInput.slice(0, -1);
        this.inputText.setText(this.currentInput);
        this.updateCursorPosition();
        return;
      }

      // Only accept printable characters
      if (e.key.length === 1 && this.currentInput.length < HEARTH_WHISPER_MAX_LENGTH) {
        this.currentInput += e.key;
        this.inputText.setText(this.currentInput);
        this.updateCursorPosition();
      }
    };

    window.addEventListener('keydown', this.keyListener, { capture: true });
  }

  deactivateInput(): void {
    if (!this.inputActive) return;
    this.inputActive = false;
    this.currentInput = '';
    this.inputText.setText('');
    this.inputBg.setStrokeStyle(1, C5_PAPER_DK);
    this.inputCursor.setVisible(false);
    this.hintText.setVisible(true);

    if (this.keyListener) {
      window.removeEventListener('keydown', this.keyListener, { capture: true });
      this.keyListener = null;
    }
  }

  private submitInput(): void {
    const content = this.currentInput.trim();
    if (content.length > 0 && content.length <= HEARTH_WHISPER_MAX_LENGTH) {
      this.onSend?.(content);
    }
    this.deactivateInput();
  }

  private createPanel(): void {
    const { height } = this.scene.scale;
    const px = MARGIN + PANEL_WIDTH / 2;
    const py = height - MARGIN - PANEL_HEIGHT / 2;

    // The chat pad is paper: whispers are authored, so they arrive on it.
    const paper = this.scene.add.graphics();
    drawPaperSlip(
      paper,
      px - PANEL_WIDTH / 2, py - PANEL_HEIGHT / 2,
      PANEL_WIDTH, PANEL_HEIGHT,
      { radius: 3, alpha: 0.93, shadow: 4 },
    );
    this.container.add(paper);

    // Title bar
    const titleY = py - PANEL_HEIGHT / 2 + 14;
    const title = this.scene.add.text(px, titleY, getString('hearth.chat.title'), {
      fontFamily: FontManager.fontFamily(),
      fontSize: '11px',
      fontStyle: 'bold',
      color: hexToCSS(C5_HANKO),
      align: 'center',
    });
    title.setOrigin(0.5, 0.5);
    this.container.add(title);

    // Input area at the bottom of the panel
    const inputY = py + PANEL_HEIGHT / 2 - INPUT_HEIGHT / 2 - 6;
    // Ruled writing line rather than a dark well -- still the same Rectangle,
    // so the focus-suppression protocol in HudState sees no change.
    this.inputBg = this.scene.add.rectangle(
      px, inputY, PANEL_WIDTH - MARGIN * 2, INPUT_HEIGHT, C5_PAPER, 0.55,
    );
    this.inputBg.setStrokeStyle(1, C5_PAPER_DK);
    this.container.add(this.inputBg);

    // Input text
    const inputTextX = px - (PANEL_WIDTH - MARGIN * 2) / 2 + 4;
    this.inputText = this.scene.add.text(inputTextX, inputY, '', {
      fontFamily: FontManager.fontFamily(),
      fontSize: '10px',
      color: hexToCSS(C5_INK),
    });
    this.inputText.setOrigin(0, 0.5);
    this.container.add(this.inputText);

    // Blinking cursor
    this.inputCursor = this.scene.add.rectangle(inputTextX, inputY, 1, 12, C5_INK);
    this.inputCursor.setOrigin(0, 0.5);
    this.inputCursor.setVisible(false);
    this.container.add(this.inputCursor);

    // Hint text
    this.hintText = this.scene.add.text(inputTextX, inputY, getString('hearth.chat.prompt'), {
      fontFamily: FontManager.fontFamily(),
      fontSize: '9px',
      fontStyle: 'italic',
      color: hexToCSS(C5_INK_DIM),
    });
    this.hintText.setOrigin(0, 0.5);
    this.hintText.setAlpha(0.6);
    this.container.add(this.hintText);

    // Position the log container above the input
    const logBaseY = py - PANEL_HEIGHT / 2 + 28;
    this.logContainer.setPosition(0, logBaseY);

    // The log container is created before the chrome, so it would otherwise
    // sit *under* the paper. The old translucent panel let the text bleed
    // through; opaque paper does not.
    this.container.bringToTop(this.logContainer);
  }

  private relayoutLines(): void {
    let y = 0;
    for (const line of this.lines) {
      line.text.setY(y);
      y += LINE_HEIGHT;
    }
  }

  private updateCursorPosition(): void {
    const textWidth = this.inputText.width;
    const inputTextX = this.inputText.x;
    this.inputCursor.setX(inputTextX + textWidth);
  }

  destroy(): void {
    this.deactivateInput();
    if (this.cursorTimer) {
      this.cursorTimer.destroy();
      this.cursorTimer = null;
    }
    for (const line of this.lines) {
      line.text.destroy();
    }
    this.lines = [];
    this.container.destroy();
  }
}
